import React from 'react';
import ExperienceItem from './ExperienceItem';
import SectionHeader from './SectionHeader';

const experienceData = [
  {
    id: 0,
    date: '2026 — present',
    title: 'Head of Growth',
    company: 'Heartbeat',
    companyUrl: 'https://www.heartbeat.chat/?utm_source=claysite&utm_medium=referral',
    intro:
      'Heartbeat is an all-in-one platform for creators and coaches to build, run, and monetize online communities, with 37,000+ communities built and $25M+ paid out to creators. I lead growth across acquisition, activation, and monetization.',
  },
  {
    id: 1,
    date: '2022 — 2026',
    title: 'Head of Marketing',
    company: 'Roll20',
    companyUrl: 'https://roll20.net',
    previousRoles: [
      { title: 'Director of Growth & Analytics', date: '2021 — 2022' },
    ],
    intro:
      'Roll20 is the leading consumer platform for online tabletop gaming, with 22M+ monthly active users. I reported to the CEO as a member of the executive team and directly managed a team of 8.',
    description: [
      'I helped grow the company from 8 million to 22 million MAU (monthly active users) by optimizing the new user funnel, publishing 20k+ SEO pages, and executing paid campaigns, holding every channel to 3:1 LTV:CAC or better.',
      'Added $3M+ in incremental ARR via retention, winback, and LTV expansion, including A/B testing the full subscription funnel and targeted pre-cancellation deflections and discounts.',
      'Propelled a 25%+ increase in sign-up conversion by removing friction in the registration flow and redesigning the post-account-creation experience.',
      'Created an AI-powered "data analyst" that delivers accurate, multi-modal business intelligence to internal users. It follows an evolving set of business logic to convert questions into performant SQL queries, runs those queries, and returns not just data but visualizations and insights. Trusted by leaders and executives at the company.',
      'Tripled daily sign-ups by launching Roll20 as an embedded "Activity" in Discord, with guerilla GTM outreach to hundreds of servers — unlocking a new distribution channel and top-of-funnel growth.',
      'Scaled SEO and video growth engines with the help of AI, doubling monthly active users, tripling impressions via short-form video, and leveraging GA4, Mixpanel, Hubspot, Stripe, Tableau, and Grafana for executive-level analytics.',
    ],
    keyMetrics: [
      { value: '3x', label: 'Monthly Active Users' },
      { value: '$3M+', label: 'Incremental ARR' },
      { value: '25%+', label: 'Sign-up Conversion Lift' },
    ],
  },
  {
    id: 2,
    date: '2020 — 2021',
    title: 'Sr. Director of Performance Marketing',
    company: 'Kinship',
    companyUrl: 'https://www.kinship.com/',
    intro:
      "At Kinship, I led performance marketing across Mars Petcare's “pet tech” portfolio of brands, including Whistle (the #1 market-leading pet wearable, now Tractive) and Wisdom Panel (pet DNA testing).",
    description: [
      'Added $4M+ in incremental revenue while managing a $40M+ annual budget across a wide portfolio of businesses.',
      'Slashed CAC by 15% through channel optimization and data-driven media allocation.',
      'Streamlined agency partnerships across geographies and time zones to accelerate execution.',
      'Pioneered a content agency supporting 20+ Mars Petcare brands with traffic-driving content.',
    ],
    keyMetrics: [
      { value: '$40M+', label: 'Budget Managed' },
      { value: '$4M+', label: 'Added Revenue' },
      { value: '15%', label: 'CAC Reduction' },
    ],
  },
  {
    id: 3,
    date: '2018 — 2020',
    title: 'Director, Customer Acquisition & SEO',
    company: 'ClassPass',
    companyUrl: 'https://classpass.com',
    intro:
      'At ClassPass, I was responsible for cost-effective customer acquisition for a two-sided marketplace and led a team of 6. Valuation grew from $650M to $1.2B during my tenure.',
    description: [
      'Added 50% incremental subscriber growth from organic and generated $750k+ ARR by launching several significant SEO and CRO overhauls and partnered with the product team to redesign most of the site with customer acquisition in mind.',
      'Optimized every detail of our App Store listings (down to the package name) to improve rankings.',
      'Optimized our referral program, testing incentives that boosted performance and engagement.',
      'Improved paid search and social efficiency, reducing paid search CAC by 20% and social CAC by 15% while sustaining acquisition volume.',
      'Led a team of channel experts across SEO, referrals, and paid media to maximize growth and ROI.',
    ],
    keyMetrics: [
      { value: '50%', label: 'Organic Subscriber Growth' },
      { value: '$750K+', label: 'Added ARR' },
      { value: '20%/15%', label: 'Paid Search/Social CAC ↓' },
    ],
  },
  {
    id: 4,
    date: '2017 — 2018',
    title: 'Director, SEO',
    company: 'Huge',
    companyUrl: 'https://www.hugeinc.com',
    previousRoles: [
      { title: 'Associate Director, SEO', date: '2016 — 2017' },
      { title: 'Lead, SEO', date: '2015 — 2016' },
    ],
    intro:
      'At Huge, I designed and implemented SEO strategies to improve visibility for iconic brands like Google, Hulu, and Verizon.',
    description: [
      'Spearheaded an SEO redesign of Hulu.com to elevate thousands of show and episode landing pages, increasing traffic by 40% within a year and out-competing other streaming sites.',
      "Led SEO strategy for Google's Think With Google site, improving rankings and traffic.",
      'Added $4M in new revenue by designing new offerings like App Store Optimization (ASO).',
    ],
    keyMetrics: [
      { value: '40%', label: 'Traffic Increase (Hulu)' },
      { value: '$4M', label: 'New Business' },
    ],
  },
  {
    id: 5,
    date: '2015 — 2017',
    title: 'Growth & Analytics Manager',
    company: 'Edgewise Realty',
    companyUrl: 'https://edgewiserealty.com/',
    description: [
      'First marketing hire at a home-buying/customization software platform.',
      'Led growth and analytics; supported Series A fundraising and national expansion.',
      'Orchestrated multi-channel campaigns and guided prospects through a long sales cycle.',
      'Pioneered A/B testing of landing pages and ad copy, driving LTV/CAC to 5x+.',
    ],
    keyMetrics: [
      { value: '5x+', label: 'LTV/CAC' },
      { value: 'Series A', label: 'Milestone' },
    ],
  },
  {
    id: 6,
    date: '2013 — 2015',
    title: 'Manager, SEO',
    company: 'Digitas Health',
    companyUrl: 'https://www.digitashealth.com',
    previousRoles: [
      { title: 'Sr. Analyst, SEO' },
      { title: 'Analyst, SEO', date: '2012 — 2013' },
    ],
    intro:
      'Digitas Health is a global marketing agency focused on healthcare and pharmaceutical brands.',
    description: [
      'Led SEO strategies for healthcare clients including AstraZeneca and Mylan.',
      'Developed new agency product offerings to secure new business.',
    ],
    keyMetrics: [{ value: '2×', label: 'Promotions' }],
  },
  {
    id: 7,
    date: '2011 — 2012',
    title: 'Growth Generalist',
    company: 'Overture Interactive',
    companyUrl: '#',
    description: [
      'Only employee at a gaming company operating casino sites (blackjack, bingo, etc.).',
      'Hustled into growth on a zero budget and learned to target high-LTV customers.',
      'Self-taught digital marketing foundations: SEO, email, referrals, paid social, frontend dev, and A/B testing.',
    ],
    keyMetrics: [
      { value: '0$', label: 'Budget' },
      { value: 'High-LTV', label: 'Targeting Focus' },
    ],
  },
];

const Experience = () => {
  return (
    <section
      id="experience"
      className="content-section pt-4 pb-2 sticky-section"
      aria-label="Work experience"
    >
      <SectionHeader id="experience" title="Experience" />

      <div>
        <ul className="group/list">
          {experienceData.map((experience, index) => (
            <ExperienceItem key={index} experience={experience} />
          ))}
        </ul>

        <div className="mt-12">
          <a
            className="inline-flex items-center text-lightest-slate font-semibold group/link hover:text-gold"
            href="/resume.pdf"
            target="_blank"
            rel="noreferrer noopener"
          >
            <span>View Full Résumé</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="ml-1 h-4 w-4 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Experience;
